import React, { useState } from 'react';
import { SketchPicker } from 'react-color';
import reactCSS from 'reactcss';
import { ColorPickerPencil } from './ColorPickerPencil';

export function ElementStrokColor(editor) {
  const { Canvaseditor } = editor;

  const [elementColorPicker, setElementColorPicker] = useState({
    displayColorPicker: false,
    color: {
      r: '0',
      g: '161',
      b: '255',
      a: '100'
    }
  });

  const [elementStrokeColorPicker, setelementStrokeColorPicker] = useState({
    displayColorPicker: false,
    color: {
      r: '98',
      g: '197',
      b: '255',
      a: '100'
    }
  });
  // Set css for elements color picker.
  const elementStyles = reactCSS({
    default: {
      color: {
        width: '36px',
        height: '14px',
        borderRadius: '2px',
        background: `rgba(${elementColorPicker.color.r}, ${elementColorPicker.color.g}, ${elementColorPicker.color.b}, ${elementColorPicker.color.a})`
      },
      swatch: {
        display: 'inline-block',
        background: `rgb(${elementColorPicker.color.r}, ${elementColorPicker.color.g}, ${elementColorPicker.color.b})`,
        width: '36px',
        height: '33px',
        borderRadius: '2px',
        marginRight: '5px',
        boxShadow: '0 0 2px rgb(16 22 26 / 30%)',
        cursor: 'pointer'
      },
      popover: {
        position: 'fixed',
        zIndex: '2'
      },
      cover: {
        position: 'fixed',
        top: '0px',
        right: '0px',
        bottom: '0px',
        left: '0px'
      }
    }
  });

  // Apply HEX color picked from the eyedropper to the element fill picker state
  // and propagate it to the active Fabric object using the same rules as the
  // regular color picker.
  const applyHexToElementFill = (hex) => {
    if (!hex || !Canvaseditor) return;

    const value = hex.trim();
    if (!value.startsWith('#') || (value.length !== 7 && value.length !== 4)) {
      return;
    }

    const expandShortHex = (v) => {
      if (v.length !== 4) return v;
      const r = v[1];
      const g = v[2];
      const b = v[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    };

    const normalized = expandShortHex(value);
    const r = parseInt(normalized.slice(1, 3), 16);
    const g = parseInt(normalized.slice(3, 5), 16);
    const b = parseInt(normalized.slice(5, 7), 16);

    setElementColorPicker({
      displayColorPicker: elementColorPicker.displayColorPicker,
      color: {
        r,
        g,
        b,
        a: elementColorPicker.color.a || 1,
      },
    });

    if (Canvaseditor.getActiveObject()) {
      let activeObject = Canvaseditor.getActiveObject();
      if (activeObject.type === "group") {
        activeObject.forEachObject((obj) => {
          if (obj.set) {
            obj.set("fill", normalized);
          }
        });
      } else {
        activeObject.set("fill", normalized);
      }
      Canvaseditor.renderAll();
      if (typeof Canvaseditor.fire === "function") Canvaseditor.fire("object:modified");
    }
  };

  // Apply HEX color picked from the eyedropper to the element stroke picker
  // state and propagate it to the active Fabric object.
  const applyHexToElementStroke = (hex) => {
    if (!hex || !Canvaseditor) return;

    const value = hex.trim();
    if (!value.startsWith('#') || (value.length !== 7 && value.length !== 4)) {
      return;
    }

    const expandShortHex = (v) => {
      if (v.length !== 4) return v;
      const r = v[1];
      const g = v[2];
      const b = v[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    };

    const normalized = expandShortHex(value);
    const r = parseInt(normalized.slice(1, 3), 16);
    const g = parseInt(normalized.slice(3, 5), 16);
    const b = parseInt(normalized.slice(5, 7), 16);

    setelementStrokeColorPicker({
      displayColorPicker: elementStrokeColorPicker.displayColorPicker,
      color: {
        r,
        g,
        b,
        a: elementStrokeColorPicker.color.a || 1,
      },
    });

    if (Canvaseditor.getActiveObject()) {
      let activeObject = Canvaseditor.getActiveObject();
      if (activeObject.type === "group") {
        activeObject.forEachObject((obj) => {
          if (obj.set) {
            obj.set("stroke", normalized);
          }
        });
      } else {
        activeObject.set("stroke", normalized);
      }

      if (Canvaseditor.getActiveObject().fromSVG !== undefined) {
        Canvaseditor.getActiveObject().set("strokeWidth", 5);
      }

      Canvaseditor.renderAll();
      if (typeof Canvaseditor.fire === "function") Canvaseditor.fire("object:modified");
    }
  };

  const elementStrokeStyles = reactCSS({
    default: {
      color: {
        width: '36px',
        height: '14px',
        borderRadius: '2px',
        background: `rgba(${elementStrokeColorPicker.color.r}, ${elementStrokeColorPicker.color.g}, ${elementStrokeColorPicker.color.b}, ${elementStrokeColorPicker.color.a})`
      },
      swatch: {
        display: 'inline-block',
        background: `rgb(${elementStrokeColorPicker.color.r}, ${elementStrokeColorPicker.color.g}, ${elementStrokeColorPicker.color.b})`,
        width: '36px',
        height: '33px',
        borderRadius: '2px',
        boxShadow: '0 0 2px rgb(16 22 26 / 30%)',
        cursor: 'pointer'
      },
      popover: {
        position: 'fixed',
        zIndex: '2'
      },
      cover: {
        position: 'fixed',
        top: '0px',
        right: '0px',
        bottom: '0px',
        left: '0px'
      }
    }
  });

  // Element color picker.
  const handleElementColorPickerClick = () => {
    setElementColorPicker({
      displayColorPicker: !elementColorPicker.displayColorPicker,
      color: {
        r: '0',
        g: '0',
        b: '0',
        a: '1'
      }
    });
  };

  const handleElementColorPickerClose = () => {
    setElementColorPicker({
      displayColorPicker: false,
      color: elementColorPicker.color
    });
  };

  const handleElementColorPickerChange = (color) => {
    if (Canvaseditor.getActiveObject()) {
      setElementColorPicker({
        displayColorPicker: true,
        color: color.rgb,
      });

      let activeObject = Canvaseditor.getActiveObject();
      // Check if the active object is a group.
      if (activeObject.type === "group") {
        // Iterate through all objects in the group.
        activeObject.forEachObject((obj) => {
          if (obj.set) {
            obj.set("fill", color.hex);
          }
        });
      } else {
        // Single object.
        activeObject.set("fill", color.hex);
      }
      Canvaseditor.renderAll();
      if (typeof Canvaseditor.fire === "function") Canvaseditor.fire("object:modified");
    }
  };

  // Stroke color picker for element.
  const handleElementStrokeColorPickerClick = () => {
    setelementStrokeColorPicker({
      displayColorPicker: !elementStrokeColorPicker.displayColorPicker,
      color: {
        r: '0',
        g: '0',
        b: '0',
        a: '1'
      }
    });
  };

  const handleElementStrokeColorPickerClose = () => {
    setelementStrokeColorPicker({
      displayColorPicker: false,
      color: elementStrokeColorPicker.color
    });
  };

  const handleElementStrokeColorPickerChange = (color) => {
    if (Canvaseditor.getActiveObject()) {
      setelementStrokeColorPicker({
        displayColorPicker: true,
        color: color.rgb,
      });

      let activeObject = Canvaseditor.getActiveObject();
      // Check if the active object is a group.
      if (activeObject.type === "group") {
        // Iterate through all objects in the group.
        activeObject.forEachObject((obj) => {
          if (obj.set) {
            obj.set("stroke", color.hex);
          }
        });
      } else {
        // Single object.
        activeObject.set("stroke", color.hex);
      }
      
      if (Canvaseditor.getActiveObject().fromSVG !== undefined) {
        Canvaseditor.getActiveObject().set("strokeWidth", 5);
      }
      
      Canvaseditor.renderAll();
      if (typeof Canvaseditor.fire === "function") Canvaseditor.fire("object:modified");
    }
  };

  const handleEyedropperElementFillPicked = (hex) => {
    applyHexToElementFill(hex);
  };

  const handleEyedropperElementStrokePicked = (hex) => {
    applyHexToElementStroke(hex);
  };

  const elemRgbToHex = (r, g, b) =>
    "#" + [r, g, b].map((x) => Math.round(Number(x)).toString(16).padStart(2, "0")).join("");

  const elemRgbFill = elementColorPicker.color || { r: 0, g: 0, b: 0, a: 1 };
  const elemHexFill = elemRgbToHex(Number(elemRgbFill.r), Number(elemRgbFill.g), Number(elemRgbFill.b)).replace("#", "").toUpperCase();

  const toColorObj = (rgb) => ({ rgb, hex: elemRgbToHex(rgb.r, rgb.g, rgb.b) });

  const handleElemFillHexChange = (e) => {
    const v = e.target.value.replace(/[^0-9A-Fa-f]/g, "").slice(0, 6);
    if (v.length === 6) {
      const nextRgb = { r: parseInt(v.slice(0, 2), 16), g: parseInt(v.slice(2, 4), 16), b: parseInt(v.slice(4, 6), 16), a: Number(elemRgbFill.a) || 1 };
      setElementColorPicker({ displayColorPicker: elementColorPicker.displayColorPicker, color: nextRgb });
      handleElementColorPickerChange(toColorObj(nextRgb));
    }
  };

  const handleElemFillRgbaChange = (key, val) => {
    const n = key === "a" ? Math.min(100, Math.max(0, parseInt(val, 10) || 0)) / 100 : Math.min(255, Math.max(0, parseInt(val, 10) || 0));
    const nextRgb = { ...elemRgbFill, [key]: n };
    setElementColorPicker({ displayColorPicker: elementColorPicker.displayColorPicker, color: nextRgb });
    handleElementColorPickerChange(toColorObj(nextRgb));
  };

  const elemRgbStroke = elementStrokeColorPicker.color || { r: 0, g: 0, b: 0, a: 1 };
  const elemHexStroke = elemRgbToHex(Number(elemRgbStroke.r), Number(elemRgbStroke.g), Number(elemRgbStroke.b)).replace("#", "").toUpperCase();

  const handleElemStrokeHexChange = (e) => {
    const v = e.target.value.replace(/[^0-9A-Fa-f]/g, "").slice(0, 6);
    if (v.length === 6) {
      const nextRgb = { r: parseInt(v.slice(0, 2), 16), g: parseInt(v.slice(2, 4), 16), b: parseInt(v.slice(4, 6), 16), a: Number(elemRgbStroke.a) || 1 };
      setelementStrokeColorPicker({ displayColorPicker: elementStrokeColorPicker.displayColorPicker, color: nextRgb });
      handleElementStrokeColorPickerChange(toColorObj(nextRgb));
    }
  };

  const handleElemStrokeRgbaChange = (key, val) => {
    const n = key === "a" ? Math.min(100, Math.max(0, parseInt(val, 10) || 0)) / 100 : Math.min(255, Math.max(0, parseInt(val, 10) || 0));
    const nextRgb = { ...elemRgbStroke, [key]: n };
    setelementStrokeColorPicker({ displayColorPicker: elementStrokeColorPicker.displayColorPicker, color: nextRgb });
    handleElementStrokeColorPickerChange(toColorObj(nextRgb));
  };

  return (
    <div className="font-color-container">
      <div style={{ float: 'left' }}>
        <div style={elementStyles.swatch} onClick={handleElementColorPickerClick}>
          <div style={elementStyles.color} />
        </div>
        {elementColorPicker.displayColorPicker ? (
          <div style={elementStyles.popover}>
            <div style={elementStyles.cover} onClick={handleElementColorPickerClose} />
            <div className="color-picker-fields-wrap" style={{ position: "relative" }}>
              <SketchPicker color={elementColorPicker.color} onChange={handleElementColorPickerChange} />
              <div className="color-picker-fields-inner">
                <div className="color-picker-hex-row">
                  <span className="color-picker-hex-label">HEX</span>
                  <input type="text" className="color-picker-hex-input" value={elemHexFill} onChange={handleElemFillHexChange} onBlur={(e) => { const v = e.target.value.replace(/[^0-9A-Fa-f]/g, "").padEnd(6, "0").slice(0, 6); if (v.length === 6) { const nextRgb = { r: parseInt(v.slice(0,2),16), g: parseInt(v.slice(2,4),16), b: parseInt(v.slice(4,6),16), a: Number(elemRgbFill.a)||1 }; setElementColorPicker({ displayColorPicker: elementColorPicker.displayColorPicker, color: nextRgb }); handleElementColorPickerChange(toColorObj(nextRgb)); } }} />
                  <ColorPickerPencil Canvaseditor={Canvaseditor} mode="fill" applyToActiveObject={false} onColorPicked={handleEyedropperElementFillPicked} />
                </div>
                <div className="color-picker-rgba-row">
                  {["r","g","b","a"].map((k) => (
                    <div key={k} style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: "#666", textAlign: "center", marginBottom: 2, textTransform: "uppercase" }}>{k}</div>
                      <input type="number" min={0} max={k==="a"?100:255} value={k==="a"?Math.round((Number(elemRgbFill.a)||1)*100):(Number(elemRgbFill[k])||0)} onChange={(e) => handleElemFillRgbaChange(k, e.target.value)} style={{ width: "100%", padding: "4px", border: "1px solid #ccc", borderRadius: 3, fontSize: 11, boxSizing: "border-box" }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <div style={{ display: 'inline-block' }}>
        <div style={elementStrokeStyles.swatch} onClick={handleElementStrokeColorPickerClick}>
          <div style={elementStrokeStyles.color} />
        </div>
        {elementStrokeColorPicker.displayColorPicker ? (
          <div style={elementStrokeStyles.popover}>
            <div style={elementStrokeStyles.cover} onClick={handleElementStrokeColorPickerClose} />
            <div className="color-picker-fields-wrap" style={{ position: "relative" }}>
              <SketchPicker color={elementStrokeColorPicker.color} onChange={handleElementStrokeColorPickerChange} />
              <div className="color-picker-fields-inner">
                <div className="color-picker-hex-row">
                  <span className="color-picker-hex-label">HEX</span>
                  <input type="text" className="color-picker-hex-input" value={elemHexStroke} onChange={handleElemStrokeHexChange} onBlur={(e) => { const v = e.target.value.replace(/[^0-9A-Fa-f]/g, "").padEnd(6, "0").slice(0, 6); if (v.length === 6) { const nextRgb = { r: parseInt(v.slice(0,2),16), g: parseInt(v.slice(2,4),16), b: parseInt(v.slice(4,6),16), a: Number(elemRgbStroke.a)||1 }; setelementStrokeColorPicker({ displayColorPicker: elementStrokeColorPicker.displayColorPicker, color: nextRgb }); handleElementStrokeColorPickerChange(toColorObj(nextRgb)); } }} />
                  <ColorPickerPencil Canvaseditor={Canvaseditor} mode="stroke" applyToActiveObject={false} onColorPicked={handleEyedropperElementStrokePicked} />
                </div>
                <div className="color-picker-rgba-row">
                  {["r","g","b","a"].map((k) => (
                    <div key={k} style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: "#666", textAlign: "center", marginBottom: 2, textTransform: "uppercase" }}>{k}</div>
                      <input type="number" min={0} max={k==="a"?100:255} value={k==="a"?Math.round((Number(elemRgbStroke.a)||1)*100):(Number(elemRgbStroke[k])||0)} onChange={(e) => handleElemStrokeRgbaChange(k, e.target.value)} style={{ width: "100%", padding: "4px", border: "1px solid #ccc", borderRadius: 3, fontSize: 11, boxSizing: "border-box" }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
